import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import api from '../../../../api';
const novo = () => ({ uuid: '', descricao: '', ativo: true });
const mensagem = e => e.response?.data?.message || e.message || 'Não foi possível concluir a operação.';

export default function ProfissoesDialog({ open, onClose, onSelect, onChanged }) {
    const [item, setItem] = useState(novo);
    const [lista, setLista] = useState([]);
    const [filtro, setFiltro] = useState('');
    const [ativo, setAtivo] = useState('todos');
    const [pagina, setPagina] = useState(0);
    const [total, setTotal] = useState(0);
    const [versao, setVersao] = useState(0);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');
    const [excluir, setExcluir] = useState(null);
    const lock = useRef(false);
    useEffect(() => {
        if (open) { setItem(novo()); setExcluir(null); setErro(''); setSucesso(''); setPagina(0); setVersao(v => v + 1); }
    }, [open]);
    useEffect(() => {
        if (!open) return undefined;
        let vigente = true;
        setLoading(true);
        const timer = setTimeout(() => {
            api.get('/profissao/listar', { params: { q: filtro, ativo, pagina } })
                .then(({ data }) => {
                    if (!Array.isArray(data.data)) throw new Error('Resposta inválida ao listar profissões.');
                    if (vigente) { setLista(data.data); setTotal(data.total); }
                }).catch(e => { if (vigente) { setLista([]); setErro(mensagem(e)); } })
                .finally(() => { if (vigente) setLoading(false); });
        }, 200);
        return () => { vigente = false; clearTimeout(timer); };
    }, [open, filtro, ativo, pagina, versao]);
    async function executar(action) {
        if (lock.current) return;
        lock.current = true; setBusy(true); setErro(''); setSucesso('');
        try { const salvo = await action(); setVersao(v => v + 1); onChanged(salvo); }
        catch (e) { setErro(mensagem(e)); }
        finally { lock.current = false; setBusy(false); }
    }
    const salvar = () => executar(async () => {
        const body = { descricao: item.descricao, ativo: item.ativo };
        const { data } = item.uuid ? await api.patch(`/profissao/editar/${item.uuid}`, body) : await api.post('/profissao/cadastrar', body);
        setItem(novo()); setSucesso(data.message); setFiltro(''); setAtivo('todos'); setPagina(0);
        return data.data;
    });
    const remover = () => executar(async () => {
        const { data } = await api.post(`/profissao/excluir/${excluir.uuid}`);
        setExcluir(null); setItem(novo()); setPagina(0); setSucesso(data.message);
    });
    return (
        <Dialog open={open} fullWidth maxWidth="md" onClose={() => { if (!busy) onClose(); }}>
            <DialogTitle>Cadastro de profissões</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {erro && <Alert severity="error" onClose={() => setErro('')}>{erro}</Alert>}
                    {sucesso && <Alert severity="success">{sucesso}</Alert>}
                    <Typography variant="body2">Cadastro compartilhado entre as empresas. Salve a pessoa após selecionar uma profissão.</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField label={item.uuid ? 'Editar profissão' : 'Nova profissão'} value={item.descricao}
                            disabled={busy} onChange={e => setItem(p => ({ ...p, descricao: e.target.value }))} sx={{ flex: 1, minWidth: 200 }} />
                        <FormControlLabel label="Ativa" control={<Checkbox checked={!!item.ativo} disabled={busy}
                            onChange={e => setItem(p => ({ ...p, ativo: e.target.checked }))} />} />
                        <Button type="button" variant="contained" disabled={busy || !item.descricao.trim()} onClick={salvar}>Salvar profissão</Button>
                        {item.uuid && <Button type="button" disabled={busy} onClick={() => setItem(novo())}>Cancelar edição</Button>}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField label="Pesquisar" size="small" fullWidth disabled={busy} value={filtro}
                            onChange={e => { setFiltro(e.target.value); setPagina(0); }} />
                        <TextField select label="Situação" size="small" value={ativo} disabled={busy} sx={{ minWidth: 130 }}
                            onChange={e => { setAtivo(e.target.value); setPagina(0); }}>
                            <MenuItem value="todos">Todas</MenuItem><MenuItem value="true">Ativas</MenuItem><MenuItem value="false">Inativas</MenuItem>
                        </TextField>
                    </Box>
                    {loading ? <Typography>Carregando...</Typography> : !lista.length ? <Typography>Nenhuma profissão encontrada.</Typography> : lista.map(p => (
                        <Box key={p.uuid} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                            <Typography sx={{ flex: 1, minWidth: 180 }}>{p.descricao}{!p.ativo ? ' (Inativa)' : ''}</Typography>
                            <Button type="button" disabled={busy || !p.ativo} onClick={() => onSelect(p)}>Selecionar</Button>
                            <Button type="button" disabled={busy} onClick={() => { setItem(p); setExcluir(null); }}>Editar</Button>
                            <Button type="button" color="error" disabled={busy} onClick={() => setExcluir(p)}>Excluir</Button>
                        </Box>
                    ))}
                    {excluir && <Alert severity="warning" action={<Stack direction="row"><Button type="button" color="error" disabled={busy} onClick={remover}>Confirmar exclusão</Button><Button type="button" disabled={busy} onClick={() => setExcluir(null)}>Cancelar</Button></Stack>}>Excluir {excluir.descricao}?</Alert>}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button type="button" disabled={busy || loading || pagina === 0} onClick={() => setPagina(p => p - 1)}>Anterior</Button>
                        <Typography variant="body2">Página {pagina + 1} · {total} registro(s)</Typography>
                        <Button type="button" disabled={busy || loading || (pagina + 1) * 50 >= total} onClick={() => setPagina(p => p + 1)}>Próxima</Button>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions><Button type="button" disabled={busy} onClick={onClose}>Fechar</Button></DialogActions>
        </Dialog>
    );
}
